import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, UserRound, Search } from "lucide-react";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import CurrencySwitcher from "./CurrencySwitcher";
import { SearchDialog } from "./SearchDialog";
import { useAuth } from "../../context/AuthContext";

const NAV_KEYS = [
    { to: "/destinations", key: "destinations" },
    { to: "/experiences", key: "experiences" },
    { to: "/tours", key: "tours" },
    { to: "/retreats", key: "retreats" },
    { to: "/corporate", key: "corporate" },
    { to: "/honeymoons", key: "honeymoons" },
    { to: "/journal", key: "journal" },
    { to: "/loyalty", key: "rewards" },
    { to: "/about", key: "about" },
    { to: "/impact", key: "impact" },
];

export default function Header() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const location = useLocation();
    const isHome = location.pathname === "/";
    const transparent = isHome && !scrolled;
    const isCustomer = user?.role === "customer";
    const accountTo = isCustomer ? "/account" : "/login";
    const accountLabel = isCustomer ? "My Enquiries" : "Sign in";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                transparent
                    ? "bg-transparent border-b border-transparent text-primary-foreground"
                    : "bg-background/85 backdrop-blur-xl border-b border-black/5 text-foreground"
            }`}
        >
            <div className="container-page flex items-center justify-between h-20">
                <Link
                    to="/"
                    data-testid="site-logo"
                    className="flex items-center gap-3 group shrink-0"
                >
                    <Logo size={36} />
                    <span className="font-serif text-xl tracking-tight hidden sm:inline whitespace-nowrap">
                        MyEcoTour
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
                    {NAV_KEYS.map((item) =>
                        item.key === "experiences" ? (
                            <div key={item.to} className="relative group">
                                <NavLink
                                    to={item.to}
                                    data-testid="nav-experiences"
                                    className={({ isActive }) =>
                                        `text-sm tracking-wide transition-colors whitespace-nowrap ${
                                            transparent
                                                ? isActive ? "text-accent font-semibold" : "text-primary-foreground/85 hover:text-accent"
                                                : isActive ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"
                                        }`
                                    }
                                >
                                    {t(`nav.${item.key}`)}
                                </NavLink>
                                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="bg-background text-foreground border border-border shadow-2xl min-w-[250px] py-2" data-testid="experiences-submenu">
                                        <Link to="/experiences" className="block px-5 py-2.5 text-sm text-muted-foreground hover:text-primary hover:bg-secondary/70 transition-colors" data-testid="submenu-all-experiences">
                                            All Experiences
                                        </Link>
                                        <Link to="/ultra-lux-collection" className="block px-5 py-2.5 text-sm font-semibold text-primary hover:bg-secondary/70 transition-colors" data-testid="submenu-ultralux">
                                            ✦ The Ultra-Lux 51 · Rarities
                                        </Link>
                                        <Link to="/connoisseurs-atelier" className="block px-5 py-2.5 text-sm font-semibold text-primary hover:bg-secondary/70 transition-colors" data-testid="submenu-atelier">
                                            ✦ The Connoisseur's Atelier
                                        </Link>
                                        <Link to="/connoisseurs-residences" className="block px-5 py-2.5 text-sm font-semibold text-primary hover:bg-secondary/70 transition-colors" data-testid="submenu-residences">
                                            ✦ The Connoisseur's Residences
                                        </Link>
                                        <Link to="/experiences?theme=luxury" className="block px-5 py-2.5 text-sm text-muted-foreground hover:text-primary hover:bg-secondary/70 transition-colors" data-testid="submenu-luxury-theme">
                                            Luxury Experiences by Theme
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-${item.to.slice(1)}`}
                            className={({ isActive }) =>
                                `text-sm tracking-wide transition-colors whitespace-nowrap ${
                                    transparent
                                        ? isActive ? "text-accent font-semibold" : "text-primary-foreground/85 hover:text-accent"
                                        : isActive ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"
                                }`
                            }
                        >
                            {t(`nav.${item.key}`)}
                        </NavLink>
                        ),
                    )}
                </nav>

                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        aria-label="Search the site"
                        data-testid="search-open-btn"
                        onClick={() => setSearchOpen(true)}
                        className={`p-2 transition-colors ${
                            transparent ? "text-primary-foreground/85 hover:text-accent" : "text-muted-foreground hover:text-primary"
                        }`}
                    >
                        <Search size={18} strokeWidth={1.5} />
                    </button>
                    <LanguageSwitcher variant={transparent ? "dark" : "light"} />
                    <CurrencySwitcher variant={transparent ? "dark" : "light"} />
                    <Link
                        to={accountTo}
                        data-testid="header-account-link"
                        title={accountLabel}
                        className={`hidden sm:inline-flex items-center gap-1.5 text-sm tracking-wide transition-colors whitespace-nowrap ${
                            transparent
                                ? "text-primary-foreground/85 hover:text-accent"
                                : "text-muted-foreground hover:text-primary"
                        }`}
                    >
                        <UserRound size={16} strokeWidth={1.5} />
                        {accountLabel}
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="header-enquire-btn"
                        className={`hidden sm:inline-flex items-center px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors duration-300 ${
                            transparent
                                ? "bg-primary-foreground text-primary hover:bg-accent hover:text-accent-foreground"
                                : "bg-primary text-primary-foreground hover:bg-accent"
                        }`}
                    >
                        {t("nav.enquire")}
                    </Link>
                    <button
                        type="button"
                        aria-label="Toggle navigation"
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden p-2"
                    >
                        {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="lg:hidden bg-background border-t border-border">
                    <div className="container-page py-6 flex flex-col gap-5">
                        {NAV_KEYS.map((item) => (
                            <div key={item.to}>
                                <NavLink
                                    to={item.to}
                                    className="text-base text-foreground hover:text-primary transition-colors"
                                    data-testid={`mobile-nav-${item.to.slice(1)}`}
                                >
                                    {t(`nav.${item.key}`)}
                                </NavLink>
                                {item.key === "experiences" && (
                                    <>
                                        <NavLink
                                            to="/ultra-lux-collection"
                                            className="block mt-2 ml-4 text-sm text-primary font-semibold"
                                            data-testid="mobile-nav-ultralux"
                                        >
                                            ✦ The Ultra-Lux 51 · Rarities</NavLink>
                                        <NavLink
                                            to="/connoisseurs-atelier"
                                            className="block mt-2 ml-4 text-sm text-primary font-semibold"
                                            data-testid="mobile-nav-atelier"
                                        >
                                            ✦ The Connoisseur's Atelier
                                        </NavLink>
                                        <NavLink
                                            to="/connoisseurs-residences"
                                            className="block mt-2 ml-4 text-sm text-primary font-semibold"
                                            data-testid="mobile-nav-residences"
                                        >
                                            ✦ The Connoisseur's Residences
                                        </NavLink>
                                    </>
                                )}
                            </div>
                        ))}
                        <Link
                            to={accountTo}
                            data-testid="mobile-account-link"
                            className="inline-flex items-center gap-2 text-base text-foreground hover:text-primary transition-colors"
                        >
                            <UserRound size={17} strokeWidth={1.5} /> {accountLabel}
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="mobile-enquire-btn"
                            className="mt-2 inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase font-semibold"
                        >
                            {t("nav.enquire")}
                        </Link>
                    </div>
                </div>
            )}
            <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
        </header>
    );
}
